// @flow

import * as React from "react";
import axios from "axios";
import {toast} from "react-toastify";

// How long to wait for Paddle's webhook to reactivate the company after the card was updated.
const REACTIVATION_TIMEOUT_MS = 45000;

type Props = {|
  +userSettings: {is_admin?: boolean, update_url?: string},
  +style?: {},
|};

// The red "subscription frozen" banner (company.is_active === false after a failed payment), shared by the
// Dashboard, All QR Invoices and Subscriptions pages. Admins get a Reactivate button that opens Paddle's
// update-payment-card window, then polls until the payment webhook has reactivated the company.
function FrozenSubscriptionBanner({userSettings, style}: Props): React.Node {
  const [isReactivating, setIsReactivating] = React.useState(false);
  const timers = React.useRef({interval: null, timeout: null});

  const stopPolling = () => {
    clearInterval(timers.current.interval);
    clearTimeout(timers.current.timeout);
  };

  // Never keep polling after the user leaves the page.
  React.useEffect(() => stopPolling, []);

  const waitForReactivation = () => {
    timers.current.interval = setInterval(() => {
      axios.get(window.SERVER_URL + '/check_subscription_status').then((res) => {
        if (res.data.is_active === true) {
          stopPolling();
          // Full reload so every page re-reads the now-active settings.
          window.location.href = window.SERVER_URL + '/dashboard?showMessage=2';
        }
      });
    }, 2000);
    timers.current.timeout = setTimeout(() => {
      stopPolling();
      setIsReactivating(false);
      toast.error("Your card was updated, but reactivating the subscription is taking longer than expected. Please refresh this page in a minute - if it's still frozen, contact support.");
    }, REACTIVATION_TIMEOUT_MS);
  };

  const reactivate = () => {
    if (!userSettings.update_url) {
      toast.error("We couldn't open the payment update page. Please contact support.");
      return;
    }

    setIsReactivating(true);
    window.Paddle.Checkout.open({
      override: userSettings.update_url,
      closeCallback: () => {
        stopPolling();
        setIsReactivating(false);
      },
      successCallback: waitForReactivation,
    });
  };

  const isAdmin = userSettings.is_admin === true;
  return (
    <div className="app-error-banner" style={style}>
      <div>
        <div className="app-error-banner-title">⚠ Your subscription has been frozen</div>
        <div>Your Scan2Pay subscription has been frozen due to a payment method failure.{" "}
          {isAdmin ? "Update your payment information to reactivate it." : "Please ask a company admin to update the payment information."}</div>
      </div>
      {isAdmin ? (
        <button className="btn btn-primary" disabled={isReactivating} onClick={reactivate}>
          {isReactivating ? "Please wait…" : "Reactivate My Subscription"}
        </button>
      ) : null}
    </div>
  );
}

export default FrozenSubscriptionBanner;
